import { useRef } from "react";
import { Form, Formik } from "formik";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generatePath, useHistory } from "react-router";
import { ArrowLeft, Plus } from "lucide-react";
import { PageTitle } from "components/page-title";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsWrapper } from "components/permissions/permissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Field, FieldError, FieldLabel } from "components/ui/field";
import { Input } from "components/ui/input";
import { Skeleton } from "components/ui/skeleton";
import { Spinner } from "components/ui/spinner";
import { Textarea } from "components/ui/textarea";
import { createAppValidationSchema } from "components/api-keys/validation";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { ApiKey, ModifyApiKey } from "data/models";
import { createApiKey } from "data/mutations-common";
import { fetchApiProducts } from "data/queries-common";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";
import { getApiProductOptions } from "./api-product-options";
import {
  ApiAccessSelection,
  AppFormActionBar,
  FocusValidationErrors,
  UnsavedChangesWarning,
} from "./app-form-sections";

export function CreateAppPage() {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const history = useHistory();
  const queryClient = useQueryClient();
  const saved = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const apiProductsQuery = useQuery({
    queryKey: [QueryKeys.apiProducts],
    queryFn: fetchApiProducts,
  });
  const createMutation = useMutation({
    mutationFn: (values: ModifyApiKey) => createApiKey(values),
    onSuccess: (createdApp: ApiKey) => {
      queryClient.setQueryData<ApiKey[]>(
        [QueryKeys.apiKeys, identityId],
        (apps) => (apps ? [...apps, createdApp] : undefined),
      );
      saved.current = true;
      history.push({
        pathname: generatePath(Routes.developerApiKeysEditKey, {
          keyName: createdApp.name,
        }),
        state: { scrollToCredentials: true },
      });
    },
  });
  const options = getApiProductOptions(apiProductsQuery.data ?? []);

  return (
    <div className="max-w-3xl space-y-6">
      <PageTitle
        icon={Plus}
        title="Create New App"
        description="Name your app and choose the APIs it can access to get an API key."
        actions={
          <Button
            variant="ghost"
            size="sm"
            disabled={createMutation.isPending}
            onClick={() => history.push(Routes.developerApiKeys)}
          >
            <ArrowLeft />
            Back to My Apps
          </Button>
        }
      />
      <PermissionsWrapper keys={[PermissionsKeys.appsCreate]}>
        <ReactQueryErrorWrapper queries={[apiProductsQuery]} />
        {apiProductsQuery.isPending && (
          <div className="space-y-6" aria-label="Loading available APIs">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        )}
        {apiProductsQuery.isError && (
          <Button variant="outline" onClick={() => apiProductsQuery.refetch()}>
            Try again
          </Button>
        )}
        {apiProductsQuery.data && (
          <Formik<ModifyApiKey>
            key={identityId}
            initialValues={{
              name: "",
              notes: "",
              products: [],
              displayName: "",
              status: "pending",
            }}
            validationSchema={createAppValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              createMutation.mutate(
                { ...values, name: values.name.trim() },
                {
                  onSettled: () => setSubmitting(false),
                },
              );
            }}
          >
            {({ values, handleChange, handleBlur, errors, touched }) => (
              <Form ref={formRef} className="space-y-6" noValidate>
                <UnsavedChangesWarning
                  saved={saved}
                  isSaving={createMutation.isPending}
                />
                <FocusValidationErrors formRef={formRef} />
                <Card>
                  <CardHeader>
                    <CardTitle>Basic Information</CardTitle>
                    <CardDescription>
                      Give your app a name and description to help identify it.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Field>
                      <FieldLabel htmlFor="name">
                        App Name <span className="text-destructive">*</span>
                      </FieldLabel>
                      <Input
                        id="name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="e.g. My Address Lookup Service"
                        maxLength={60}
                        required
                        disabled={createMutation.isPending}
                        aria-invalid={touched.name && !!errors.name}
                        aria-describedby={
                          touched.name && errors.name ? "name-error" : undefined
                        }
                      />
                      {touched.name && errors.name && (
                        <FieldError id="name-error">{errors.name}</FieldError>
                      )}
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="notes">Description</FieldLabel>
                      <Textarea
                        id="notes"
                        name="notes"
                        value={values.notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="What will this app be used for?"
                        maxLength={200}
                        className="field-sizing-fixed min-h-20"
                        disabled={createMutation.isPending}
                        aria-invalid={touched.notes && !!errors.notes}
                        aria-describedby="description-help"
                      />
                      <p
                        id="description-help"
                        className="text-xs text-muted-foreground"
                      >
                        Optional. Helps you remember the purpose of this app.
                      </p>
                      {touched.notes && errors.notes && (
                        <FieldError>{errors.notes}</FieldError>
                      )}
                    </Field>
                  </CardContent>
                </Card>
                <ApiAccessSelection
                  options={options}
                  isSaving={createMutation.isPending}
                />
                <ReactQueryErrorWrapper mutations={[createMutation]} />
                <AppFormActionBar
                  isSaving={createMutation.isPending}
                  pendingStatus="Creating app…"
                  initialStatus="New app"
                >
                  <Button
                    type="button"
                    variant="outline"
                    disabled={createMutation.isPending}
                    onClick={() => history.push(Routes.developerApiKeys)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? <Spinner /> : <Plus />}
                    {createMutation.isPending ? "Creating App…" : "Create App"}
                  </Button>
                </AppFormActionBar>
              </Form>
            )}
          </Formik>
        )}
      </PermissionsWrapper>
    </div>
  );
}
